import {
  Component,
  Input,
  OnChanges,
  OnInit,
  Optional,
  SimpleChanges,
} from '@angular/core';
import { NgControl, Validators, ValidatorFn } from '@angular/forms';
import { ControlsOf, FormBuilder, FormGroup } from '@ngneat/reactive-forms';
import { delay, takeUntil } from 'rxjs/operators';

import {
  Address,
  AddressInputFields,
  AddressModel,
} from '@digital-platform/shared/domain';
import { FormValidators } from '../form-validators/form-validators';
import { NestedFormComponent } from '../nested-form/nested-form.component';

@Component({
  selector: 'digital-platform-address-input',
  templateUrl: './address-input.component.html',
  styleUrls: ['./address-input.component.scss'],
})
export class AddressInputComponent
  extends NestedFormComponent<Address, AddressInputFields>
  implements OnChanges, OnInit
{
  public form: FormGroup<ControlsOf<AddressInputFields>>;

  public countyLabel = 'County';
  public stateLabel = 'State';
  public postalCodeLabel = 'Zip Code';
  public hasValue: boolean;

  @Input() label = 'Address';
  @Input() countyRequired = false;
  @Input() tabIndex = 1;
  @Input() display = 'wide' as const;
  @Input() required: boolean;
  @Input() isReadOnly: boolean;
  @Input() profileStatus: string;
  @Input() isLegacy = false;

  constructor(
    private formBuilder: FormBuilder,
    @Optional() public controlDir: NgControl
  ) {
    super(controlDir);

    this.buildForm(AddressModel.DEFAULT_CONFIG);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.label = this.label || 'Address';
    this.tabIndex = this.tabIndex || 1;
    this.display = this.display || 'wide';
    this.countyRequired = this.countyRequired || false;
    this.form.get('county').updateValueAndValidity();
    if (changes?.required?.currentValue) {
      this.required = changes?.required?.currentValue;
    }

    if (changes.profileStatus?.currentValue === 'Not Submitted') {
      this.form.controls.addressLine1.removeValidators(Validators.required);
      this.form.controls.city.removeValidators(Validators.required);
      this.form.controls.country.removeValidators(Validators.required);
      this.form.controls.stateCode.removeValidators(Validators.required);
      this.form.controls.postalCode.removeValidators(Validators.required);
      this.form.updateValueAndValidity();
    } else {
      if (this.form) {
        this.form.controls.addressLine1.addValidators(
          this.getAddressLineValidation()
        );
        this.form.controls.city.addValidators(this.getAddressLineValidation());
        this.form.controls.country.addValidators(this.getCountryValidation());
        this.form.controls.stateCode.addValidators(
          this.getStateCodeValidation()
        );
        this.form.controls.postalCode.addValidators(
          this.getPostalCodeValidation()
        );
        this.form.updateValueAndValidity();
      }
    }
  }

  getAddressLineValidation() {
    return FormValidators.requiredIf(() => {
      if (this.isLegacy === true) return false;

      const cityVal = this.form.value?.city;
      const stateCodeVal = this.form.value?.stateCode;
      const postalCodeVal = this.form.value?.postalCode;
      const county = this.form.value?.county;
      let hasValue: boolean;
      if (cityVal || stateCodeVal || postalCodeVal || county) {
        hasValue = true;
      } else {
        hasValue = false;
      }
      return this.required || hasValue;
    });
  }

  getCityValidation() {
    return FormValidators.requiredIf(() => {
      if (this.isLegacy === true) return false;

      const addressLine1Val = this.form.value?.addressLine1;
      const stateCodeVal = this.form.value?.stateCode;
      const postalCodeVal = this.form.value?.postalCode;
      const county = this.form.value?.county;
      if (addressLine1Val || stateCodeVal || postalCodeVal || county) {
        this.hasValue = true;
      } else {
        this.hasValue = false;
      }
      return this.required || this.hasValue;
    });
  }

  getCountryValidation() {
    return FormValidators.requiredIf(() => {
      if (this.isLegacy === true) return false;

      const addressLine1Val = this.form.value?.addressLine1;
      const cityVal = this.form.value?.city;
      const postalCodeVal = this.form.value?.postalCode;
      const county = this.form.value?.county;
      if (cityVal || addressLine1Val || postalCodeVal || county) {
        this.hasValue = true;
      } else {
        this.hasValue = false;
      }
      return this.countyRequired || this.hasValue;
    });
  }

  getStateCodeValidation() {
    return FormValidators.requiredIf(() => {
      if (this.isLegacy === true) return false;

      const addressLine1Val = this.form.value?.addressLine1;
      const cityVal = this.form.value?.city;
      const postalCodeVal = this.form.value?.postalCode;
      const county = this.form.value?.county;
      if (cityVal || addressLine1Val || postalCodeVal || county) {
        this.hasValue = true;
      } else {
        this.hasValue = false;
      }
      return this.required || this.hasValue;
    });
  }

  getPostalCodeValidation() {
    return FormValidators.requiredIf(() => {
      if (this.isLegacy === true) return false;

      const addressLine1Val = this.form.value?.addressLine1;
      const cityVal = this.form.value?.city;
      const stateCodeVal = this.form.value?.stateCode;
      const county = this.form.value?.county;
      if (cityVal || stateCodeVal || addressLine1Val || county) {
        this.hasValue = true;
      } else {
        this.hasValue = false;
      }
      return this.required || this.hasValue;
    });
  }

  setDisabledState(isDisabled: boolean) {
    if (this.form) {
      if (isDisabled) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }

  public mapInputValue(value: Address): AddressInputFields {
    let {
      addressLine1,
      addressLine2,
      addressLine3,
      city,
      county,
      stateCode,
      postalCode,
      postalCodeSuffix,
      country,
    } = value;

    if (!country) {
      country = 'US';
    }

    if (
      country === 'US' &&
      postalCode &&
      postalCodeSuffix &&
      postalCode.length === 5
    ) {
      postalCode = `${postalCode}-${postalCodeSuffix}`;
      postalCodeSuffix = '';
    }

    addressLine1 = addressLine1 || '';
    addressLine2 = addressLine2 || '';
    addressLine3 = addressLine3 || '';
    city = city || '';
    county = county || '';
    stateCode = stateCode || '';

    return {
      addressLine1,
      addressLine2,
      addressLine3,
      city,
      county,
      stateCode,
      country,
      postalCode,
      postalCodeSuffix,
    };
  }

  public mapOutputValue(value: AddressInputFields): Address {
    let postalCode = value.postalCode;
    let postalCodeSuffix = value.postalCodeSuffix;
    if (value.country === 'US' && postalCode && postalCode.indexOf('-') >= 0) {
      const parts = postalCode.split('-');
      postalCode = parts[0];
      postalCodeSuffix = parts[1];
    }

    return {
      ...value,
      postalCode,
      postalCodeSuffix,
    };
  }

  private buildForm(value: Address) {
    this.form = this.formBuilder.group({
      addressLine1: this.formBuilder.control(
        value?.addressLine1,
        this.getAddressLineValidation()
      ),
      addressLine2: [value?.addressLine2],
      addressLine3: [value?.addressLine3],
      city: [
        value?.city,
        [this.getCityValidation(), Validators.pattern('^[a-zA-Z ]*$')],
      ],
      county: [value?.county, this.getCountryValidation()],
      stateCode: [value?.stateCode, this.getStateCodeValidation()],
      postalCode: [
        value?.postalCode,
        [this.getPostalCodeValidation(), this.validatePostalCode],
      ],
      postalCodeSuffix: [value?.postalCodeSuffix],
      country:
        this.isLegacy === false
          ? [value?.country, [Validators.required]]
          : [value?.country],
    });

    const addressLine1 = this.form.get('addressLine1');
    const city = this.form.get('city');
    const stateCode = this.form.get('stateCode');
    const country = this.form.get('country');
    const postalCode = this.form.get('postalCode');
    const county = this.form.get('county');
    this.form
      .select((state) => state.country)
      .pipe(
        delay(1), // delay here lets the call stack clear
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        // prevents state corruption on initial load
        if (country.touched) {
          stateCode.markAsTouched();
          postalCode.markAsTouched();
          stateCode.updateValueAndValidity();
          postalCode.updateValueAndValidity();
          stateCode.setValue(null);
          postalCode.setValue(null);
        }
      });
    this.form
      .select((state) => state.addressLine1)
      .subscribe((n) => {
        city.updateValueAndValidity();
        stateCode.updateValueAndValidity();
        postalCode.updateValueAndValidity();
        county.updateValueAndValidity();
      });

    this.form
      .select((state) => state.city)
      .subscribe((n) => {
        addressLine1.updateValueAndValidity();
        stateCode.updateValueAndValidity();
        postalCode.updateValueAndValidity();
        county.updateValueAndValidity();
      });

    this.form
      .select((state) => state.stateCode)
      .subscribe((n) => {
        addressLine1.updateValueAndValidity();
        city.updateValueAndValidity();
        postalCode.updateValueAndValidity();
        county.updateValueAndValidity();
      });

    this.form
      .select((state) => state.postalCode)
      .subscribe((n) => {
        addressLine1.updateValueAndValidity();
        stateCode.updateValueAndValidity();
        city.updateValueAndValidity();
        county.updateValueAndValidity();
      });

    this.form
      .select((state) => state.county)
      .subscribe((n) => {
        addressLine1.updateValueAndValidity();
        stateCode.updateValueAndValidity();
        city.updateValueAndValidity();
        postalCode.updateValueAndValidity();
        county.updateValueAndValidity();
      });

    this.form.value$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((address) => this.updateLabels(address));
  }

  private updateLabels(address: Address) {
    switch (address?.country) {
      case 'US':
        this.countyLabel = 'County';
        this.stateLabel = 'State';
        this.postalCodeLabel = 'Zip Code';
        break;
      case 'CA':
        this.countyLabel = 'Census Geographical Unit';
        this.stateLabel = 'Province';
        this.postalCodeLabel = 'Postal Code';
        break;
      case 'MX':
        this.countyLabel = 'Municipio';
        this.stateLabel = 'State';
        this.postalCodeLabel = 'Postal Code';
        break;
    }
  }

  hasError = (control: string, error: string) => {
    if (
      this.form.controls[control].touched &&
      this.form.controls[control].hasError(error)
    ) {
      this.form.updateValueAndValidity();
      return true;
    }
    return false;
  };

  // we use an arrow function here to maintain `this` scope to the component
  validatePostalCode: ValidatorFn = (c) => {
    if (!this.form) {
      return null;
    }

    const country = this.form.get('country').value;

    switch (country) {
      case 'US':
        return Validators.pattern('^[0-9]{5}(-[0-9]{4})?$')(c);
      case 'CA':
        return Validators.pattern('^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]')(c);
      case 'MX':
        return Validators.pattern('^[0-9]{5}$')(c);
    }

    return null;
  };

  public postalCodePatternError(): string {
    const country = this.form?.get('country').value;

    switch (country) {
      case 'US':
        return `${this.postalCodeLabel} format must be <strong>00000</strong> or <strong>00000-000</strong>`;
      case 'CA':
        return `${this.postalCodeLabel} format must be <strong>A0A 0A0</strong>`;
      case 'MX':
        return `${this.postalCodeLabel} format must be <strong>00000</strong>`;
    }

    return `${this.postalCodeLabel} format is incorrect`;
  }
}
